import { Link } from 'react-router-dom';

import {
  Magnetic,
  MediaFrame,
  Parallax,
  PointerParallax,
  ScrollReveal,
  SplitReveal,
} from '@/components/motion';
import { ADVISOR_CHANNELS } from '@/data/admissions';
import { Button, cn } from '@ibas/ui';

/** Warm editorial photo of advisers in conversation (image only, no video). */
const ADVISOR_IMAGE = '/media/images/people/business-meeting-portrait.webp';

/**
 * FindAdvisor (/admissions §2) — "Find your admission advisor". A warm,
 * two-column invitation to reach the admissions team: copy plus the real,
 * verified contact channels on the left, and a scroll/pointer-parallax portrait
 * on the right. Every channel value comes straight from ADVISOR_CHANNELS — the
 * email and phone become clearly interactive links; the address stays plain
 * text (no href). Nothing about advisers is invented.
 *
 * Motion: SplitReveal headline, ScrollReveal (child-stagger) for the channel
 * rows, Parallax + PointerParallax depth on the media plate, and a Magnetic
 * gold CTA. All primitives are reduced-motion safe.
 */
export function FindAdvisor() {
  return (
    <section className="border-t border-border bg-muted/30 py-16 md:py-24">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
        {/* ── LEFT: copy + channels ───────────────────────────────────────── */}
        <div className="max-w-xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Find your admission advisor
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            Talk to someone who knows the route.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-muted-foreground md:text-lg">
              Our admissions team supports you with applications, documents and the admission
              process before enrolment — so you can start with confidence.
            </p>
          </ScrollReveal>

          <ScrollReveal stagger={0.1} className="mt-9 flex flex-col gap-4">
            {ADVISOR_CHANNELS.map((channel) => (
              <div
                key={channel.label}
                className="group flex items-start gap-4 rounded-xl border border-border bg-background p-5 transition-shadow duration-500 hover:shadow-lg motion-reduce:transition-none"
              >
                <div className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-navy/5 text-brand-navy">
                  <channel.Icon aria-hidden className="size-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                    {channel.label}
                  </p>
                  {channel.href ? (
                    <a
                      href={channel.href}
                      data-cursor
                      className="mt-1 inline-block font-display text-lg leading-tight font-semibold text-foreground underline decoration-brand-gold/60 decoration-2 underline-offset-4 transition-colors duration-300 hover:text-brand-navy hover:decoration-brand-gold motion-reduce:transition-none"
                    >
                      {channel.value}
                    </a>
                  ) : (
                    <p className="mt-1 font-display text-lg leading-tight font-semibold text-foreground">
                      {channel.value}
                    </p>
                  )}
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {channel.note}
                  </p>
                </div>
              </div>
            ))}
          </ScrollReveal>

          <ScrollReveal from="up" delay={0.15}>
            <div className="mt-9">
              <Magnetic strength={0.35} radius={130}>
                <Button asChild variant="primary" size="lg" data-cursor>
                  <Link to="/apply">Start your application</Link>
                </Button>
              </Magnetic>
            </div>
          </ScrollReveal>
        </div>

        {/* ── RIGHT: warmth photo with depth ──────────────────────────────── */}
        <Parallax speed={0.12} className={cn('mx-auto w-full max-w-md lg:max-w-none')}>
          <PointerParallax strength={16}>
            <div data-parallax data-depth="0.7">
              <MediaFrame
                image={ADVISOR_IMAGE}
                alt="Two people in business attire smiling together during a meeting."
                aspect="aspect-[4/5]"
                overlay={40}
                rounded="rounded-2xl"
                position="50% 30%"
              />
            </div>
          </PointerParallax>
        </Parallax>
      </div>
    </section>
  );
}
